/**
 * The agents at work without a model: the design's «Subagents» run (Figma 412:114248) played on the open project. The
 * layout agent measures the room, the catalog agent looks the furniture up in the catalog, the style agent reads the
 * finishes, the decor agent picks the small things — each step on the project's own numbers and the catalog's own
 * answers, each agent's pointer on what it works on (the demo bots, docs/collaboration.md «Demo bots»). Nothing is
 * written to the document: a run shows how the agents work together and that their parts answer. The chat runs it
 * when no model is connected, and on «Симуляция агентов» (docs/agents.md «Simulation and health check»).
 */
import type { Item, Level, Room, Wall } from '../core/hp/types';
import { findCatalogList, loadList, type CatalogItem } from '../state/catalog';
import { DEMO_AGENTS, type DemoTarget } from '../collaboration/demoBots';
import { roomWalls } from './layout';
import { roomOfItem, surfaceLabel } from './sceneSummary';
import { formatNumber, plural, t } from '../i18n/I18n';

/** One step of an agent: what its card says while it runs and once done, where its pointer rests, how long it takes. */
export interface SimulationStep {
  running: string;
  /** The line once done, or the work that finds it (a catalog request); a thrown error is the step's failure. */
  done: string | ((signal: AbortSignal) => Promise<string>);
  target: DemoTarget | null;
  /** An editor holds the item it works on. */
  hold?: boolean;
  /** Milliseconds at pace 1; work that takes longer keeps the step running until it answers. */
  ms: number;
}
export interface SimulationAgent { id: string; name: string; color: string; icon: string; start: number; steps: SimulationStep[] }
export interface SimulationPlan {
  request: string; room: Room | null; agents: SimulationAgent[];
  /** What the steps learn as they run — the catalog's pick — for the answer. */
  found: { picked: string | null };
}
export type StepOutcome = { status: 'done' | 'error'; summary: string; detail?: string };
export interface SimulationHooks {
  /** A step starts: its card line appears turning, the agent's pointer goes to the target. */
  begin(agent: SimulationAgent, stepId: string, step: SimulationStep): void;
  finish(agent: SimulationAgent, stepId: string, outcome: StepOutcome): void;
  /** The agent is through: its pointer may roam again. */
  release(agent: SimulationAgent): void;
}
/** The catalog as the simulation asks it: a list by its name in the tree (`Sofa`, `Coffee table`). */
export type CatalogLookup = (listName: string, signal: AbortSignal) => Promise<{ title: string; items: CatalogItem[]; more: boolean } | null>;

export const catalogLookup: CatalogLookup = async listName => {
  const list = findCatalogList(listName);
  if (!list) return null;
  const page = await loadList(list);
  return { title: list.title, items: page.items, more: page.more };
};

const metres = (value: number) => formatNumber(value, 1);
const centimetres = (value: number) => Math.round(value * 100);
const lengthOf = (wall: Wall) => Math.hypot(wall.end.x - wall.start.x, wall.end.z - wall.start.z);
const extent = (room: Room) => {
  const xs = room.polygon.map(point => point.x), zs = room.polygon.map(point => point.z);
  return { width: Math.max(...xs) - Math.min(...xs), depth: Math.max(...zs) - Math.min(...zs) };
};
const lower = (text: string) => text.charAt(0).toLocaleLowerCase() + text.slice(1);
/** The catalog's many «Модель» cards say nothing by their title: those go by their size. */
const modelLabel = (item: CatalogItem) => {
  const width = centimetres(item.size[0]), depth = centimetres(item.size[2]);
  // i18n-ignore: the catalog's own titles
  return /^(модель|model)?$/i.test(item.title.trim()) ? t('#AgentSimModelBySize', width, depth) : t('#AgentSimModelTitled', item.title.trim(), width, depth);
};
/** A surface's name for a person: the caption or file name, a colour as it is, «по умолчанию» for none. */
const finish = (label: string) => label === 'default' ? t('#AgentSimFinishDefault') : label === 'custom image' ? t('#AgentSimFinishCustom') : label;
const itemName = (item: Item) => {
  const caption = (item.caption ?? '').trim();
  return caption && caption.length <= 32 && !/^[\w-]+\/\d+/.test(caption) ? caption : t('#AgentSimItem');
};

/** The catalog list a request is about: sofas unless it names beds, armchairs, tables or rugs. */
export function catalogListFor(request: string): string {
  const text = request.toLowerCase();
  // i18n-ignore-start: words matched in what a person typed, in either language
  if (/кроват|спальн|bed/.test(text)) return 'Bed';
  if (/кресл|armchair/.test(text)) return 'Armchair';
  if (/обеден|кухн|dining|kitchen/.test(text)) return 'DiningTables';
  if (/ков[её]р|rug|carpet/.test(text)) return 'Rugs';
  // i18n-ignore-end
  return 'Sofa';
}

/** The room a request names, else the largest one of the floor. */
function roomFor(level: Level, request: string): Room | null {
  const rooms = level.rooms.filter(room => room.polygon.length >= 3);
  const text = request.toLowerCase();
  const named = rooms.find(room => room.name && room.name.trim().length > 2 && text.includes(room.name.trim().toLowerCase()));
  return named ?? [...rooms].sort((a, b) => b.area - a.area)[0] ?? null;
}

/**
 * The run for this floor and request: four agents side by side, starting a moment apart, their steps laid out on the
 * room the request is about. Pure but for the catalog requests its steps make when they run.
 */
export function planSimulation(level: Level, request: string, lookup: CatalogLookup = catalogLookup): SimulationPlan {
  const room = roomFor(level, request);
  const items = level.items.filter(item => item.placement !== 'ceiling' && (!room || roomOfItem(level, item)?.index === room.index));
  const byArea = [...items].sort((a, b) => b.size.w * b.size.d - a.size.w * a.size.d);
  // i18n-ignore-start: words matched in the items' captions, in either language
  const biggest = byArea[0], sofa = items.find(item => /диван|sofa|couch/i.test(item.caption ?? '')) ?? biggest;
  const table = items.find(item => /стол|table/i.test(item.caption ?? '')) ?? byArea[1] ?? biggest;
  // i18n-ignore-end
  const walls = (room ? roomWalls(level, room) : level.walls).slice().sort((a, b) => lengthOf(b) - lengthOf(a));
  const longest = walls[0], across = walls.find(wall => wall !== longest) ?? longest;
  const roomTarget: DemoTarget | null = room ? { kind: 'room', index: room.index } : longest ? { kind: 'wall', id: longest.id } : null;
  const itemTarget = (item: Item | undefined): DemoTarget | null => item ? { kind: 'item', id: item.id } : roomTarget;
  const wallTarget = (wall: Wall | undefined): DemoTarget | null => wall ? { kind: 'wall', id: wall.id } : roomTarget;
  const size = room && extent(room);
  const listName = catalogListFor(request);
  const found: SimulationPlan['found'] = { picked: null };
  let picked: CatalogItem | null = null;
  const agent = (index: number, start: number, steps: SimulationStep[]): SimulationAgent => ({ ...DEMO_AGENTS[index], start, steps });
  return {
    request, room, found,
    agents: [
      agent(0, 0, [
        { running: t('#AgentSimMeasuring'), target: roomTarget, ms: 2600,
          done: room && size ? room.name?.trim() ? t('#AgentSimMeasuredNamed', room.name.trim(), metres(size.width), metres(size.depth), metres(room.area)) : t('#AgentSimMeasured', metres(size.width), metres(size.depth), metres(room.area)) : t('#AgentSimNoRooms') },
        { running: t('#AgentSimMarkingZones'), target: wallTarget(longest), ms: 3000,
          done: items.length ? t('#AgentSimZonesMarked', plural('#AgentCountItems', items.length)) : t('#AgentSimRoomEmpty') },
        { running: t('#AgentSimAxis'), target: itemTarget(biggest), hold: !!biggest, ms: 2800,
          done: longest ? t('#AgentSimAxisWall', metres(lengthOf(longest))) : t('#AgentSimAxisCenter') },
      ]),
      agent(1, 450, [
        { running: t('#AgentSimSearchingModels'), target: roomTarget, ms: 2400,
          done: async signal => {
            const list = await lookup(listName, signal);
            if (!list) throw new Error(t('#AgentSimNoSection', listName));
            picked = list.items.find(item => item.converted) ?? list.items[0] ?? null;
            if (picked) found.picked = t('#AgentSimPickedFrom', modelLabel(picked), list.title);
            return list.items.length ? t('#AgentSimFoundModels', `${list.items.length}${list.more ? '+' : ''}`, list.title) : t('#AgentSimSectionEmpty', list.title);
          } },
        { running: t('#AgentSimChoosingModel'), target: itemTarget(sofa), hold: !!sofa, ms: 2800,
          done: () => Promise.resolve(picked ? t('#AgentSimPicked', modelLabel(picked))
            : sofa ? t('#AgentSimKeptNoChange', itemName(sofa)) : t('#AgentSimChoiceDeferred')) },
        { running: t('#AgentSimCurtainFabric'), target: wallTarget(across), ms: 2500, done: t('#AgentSimCurtainFabricDone') },
      ]),
      agent(2, 900, [
        { running: t('#AgentSimPalette'), target: roomTarget, ms: 3200,
          done: t('#AgentSimPaletteDone', finish(surfaceLabel(room?.floorSurface)), finish(surfaceLabel(longest?.frontSurface))) },
        { running: t('#AgentSimCurtainTone'), target: wallTarget(longest), ms: 3400, done: t('#AgentSimCurtainToneDone') },
        { running: t('#AgentSimAccents'), target: itemTarget(sofa), hold: !!sofa, ms: 4200, done: t('#AgentSimAccentsDone') },
      ]),
      agent(3, 1350, [
        { running: t('#AgentSimCoffeeTable'), target: itemTarget(table), ms: 3000,
          done: async signal => {
            const list = await lookup('Coffee table', signal);
            const choice = list?.items.find(item => item.converted) ?? list?.items[0];
            return choice ? t('#AgentSimCoffeeTablePicked', modelLabel(choice)) : table ? t('#AgentSimKept', itemName(table)) : t('#AgentSimCoffeeTableLater');
          } },
        { running: t('#AgentSimCushions'), target: itemTarget(sofa), ms: 2600, done: sofa ? t('#AgentSimCushionsDone') : t('#AgentSimCushionsLater') },
      ]),
    ],
  };
}

const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
  const timer = setTimeout(() => { signal.removeEventListener('abort', stop); resolve(); }, Math.max(0, ms));
  const stop = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
  signal.addEventListener('abort', stop, { once: true });
});

/**
 * Plays the plan: the agents side by side, each step shown turning for its time (or until its work answers) and then
 * ticked or failed. `pace` scales the times (1 in the chat, a tiny one in tests). Resolves with every step's outcome;
 * an abort rejects with the AbortError after releasing the agents.
 */
export async function runSimulation(plan: SimulationPlan, hooks: SimulationHooks, signal: AbortSignal, pace = 1): Promise<Map<string, StepOutcome[]>> {
  const outcomes = new Map<string, StepOutcome[]>(plan.agents.map(agent => [agent.id, []]));
  const work = plan.agents.map(async agent => {
    try {
      await sleep(agent.start * pace, signal);
      for (const [index, step] of agent.steps.entries()) {
        const stepId = `${agent.id}:${index}`;
        hooks.begin(agent, stepId, step);
        let outcome: StepOutcome;
        try {
          const [summary] = await Promise.all([typeof step.done === 'string' ? Promise.resolve(step.done) : step.done(signal), sleep(step.ms * pace, signal)]);
          outcome = { status: 'done', summary };
        } catch (error) {
          if (signal.aborted) throw error;
          outcome = { status: 'error', summary: t('#AgentSimStepFailed', step.running), detail: error instanceof Error ? error.message : String(error) };
        }
        outcomes.get(agent.id)!.push(outcome);
        hooks.finish(agent, stepId, outcome);
      }
    } finally { hooks.release(agent); }
  });
  await Promise.all(work);
  return outcomes;
}

/** The coordinator's words after the run: what each agent found, and that the project stayed as it was. */
export function simulationAnswer(plan: SimulationPlan, outcomes: Map<string, StepOutcome[]>): string {
  const last = (id: string, index: number) => outcomes.get(id)?.[index];
  const line = (id: string, index: number) => { const outcome = last(id, index); return outcome?.status === 'done' ? lower(outcome.summary) : null; };
  const failed = [...outcomes.values()].flat().filter(outcome => outcome.status === 'error').length;
  // The room as the answer names it: its own name, else by its size.
  const room = plan.room, name = room?.name?.trim();
  const picked = plan.found.picked;
  const lines = [
    !room ? t('#AgentSimAnswerFloor', plan.request) : name ? t('#AgentSimAnswerNamedRoom', name, plan.request) : t('#AgentSimAnswerRoom', metres(room.area), plan.request),
    line('bot:layout', 0) && t('#AgentSimAnswerLayout', [line('bot:layout', 0), line('bot:layout', 2)].filter(Boolean).join('; ')),
    picked ? t('#AgentSimAnswerCatalogPicked', picked) : t('#AgentSimAnswerCatalog', line('bot:catalog', 0) ?? t('#AgentSimCatalogSilent')),
    t('#AgentSimAnswerStyle', [line('bot:style', 0), line('bot:style', 2)].filter(Boolean).join('; ') || t('#AgentSimPaletteUnread')),
    t('#AgentSimAnswerDecor', [line('bot:decor', 0), line('bot:decor', 1)].filter(Boolean).join('; ') || t('#AgentSimDecorUnfinished')),
    failed ? t('#AgentSimAnswerFailedSteps', failed) : '',
    t('#AgentSimAnswerNote'),
  ];
  return lines.filter(Boolean).join('\n');
}
